import 'swiper/css';
import 'swiper/css/navigation';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, IconButton, styled, useTheme } from '@mui/material';
import { useId } from 'react';
import { Link } from 'react-router';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import routes from '@/assets/constants/routes-no.ts';
import type { Competition } from '@/lib/api/requests.ts';
import { CompetitionTile } from '@/pages/CompetitionWinners/CompetitionTile.tsx';
import { getConfig } from '@/pages/CompetitionWinners/config.tsx';

import { Section } from '../_components/Section.tsx';

const StyledIconButton = styled(IconButton)(() => ({
  backgroundColor: '#F0F4FA',
  color: '#031839',

  '&:disabled': {
    opacity: 0.5,
  },
}));

// SSR-safe substitute for CSS.escape; useId() outputs (e.g. ":r0:") need
// escaping to be usable inside attribute / id selectors.
const escapeIdForSelector = (id: string) => id.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);

export type CompetitionsSectionProps = {
  competitions: Competition[];
};

export const CompetitionsSection: React.FC<CompetitionsSectionProps> = ({ competitions }) => {
  const theme = useTheme();
  const prevId = useId();
  const nextId = useId();

  return (
    <Section>
      <Box
        component={Link}
        to={routes.COMPETITION_WINNERS}
        sx={{
          color: 'inherit',
          textAlign: 'center',
          mb: 6,
        }}
        className="typography-v1-h2"
      >
        Tidligere trekninger
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          mx: -2,
        }}
      >
        <StyledIconButton id={prevId}>
          <KeyboardArrowLeft />
        </StyledIconButton>

        <Swiper
          slidesPerView={1}
          spaceBetween={16}
          navigation={{
            enabled: true,
            prevEl: `#${escapeIdForSelector(prevId)}`,
            nextEl: `#${escapeIdForSelector(nextId)}`,
          }}
          breakpoints={{
            [theme.breakpoints.values.sm]: {
              slidesPerView: 2,
            },
            [theme.breakpoints.values.md]: {
              slidesPerView: 3,
            },
            [theme.breakpoints.values.lg]: {
              slidesPerView: 4,
            },
          }}
          modules={[Pagination, Navigation]}
        >
          {competitions.map((competition) => (
            <SwiperSlide key={competition.slug}>
              <CompetitionTile config={getConfig(competition.slug)} competition={competition} />
            </SwiperSlide>
          ))}
        </Swiper>

        <StyledIconButton id={nextId}>
          <KeyboardArrowRight />
        </StyledIconButton>
      </Box>
    </Section>
  );
};
